import React, { useEffect, useMemo, useState } from "react";
import { useUser } from "../../context/UserContext";
import { DateRangePicker } from "../../components/DatePicker/DatePicker";
import { Header } from "../../components/Header/Header";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import { getDocs, Timestamp, where, collection, query, doc, updateDoc, arrayUnion, addDoc, getDoc, setDoc, deleteDoc, writeBatch } from "firebase/firestore";
import { db } from "../../firebase/firebaseConfig";
import { useDateRange } from "../../context/DateRangeContext";
import { AdjustmentRequest, PtoRequest } from "../../types/adjustments";
import { Employee, TimeEvent } from "../../types/employee"
import { calculateWorkedMinutesWithOvernights } from "../../utils/utils";
import { updateAccruedMinutesForDate } from "../../utils/accrual";
import { ConfirmDialog } from "../../components/ConfirmDialog/ConfirmDialog";
import { PtoDays } from "../../components/PtoDays/PtoDays";
import { Dialog, Tabs, Tab } from "@mui/material";

import Snackbar from '@mui/material/Snackbar';
import { useParams } from "react-router-dom";

dayjs.extend(isBetween);

type AdjustmentType = {
    id: string;
    type: string;
}

interface AdjustmentRequestWithId extends AdjustmentRequest {
    id: string; // Firestore doc ID
}

interface PtoRequestWithId extends PtoRequest {
    id: string; // Firestore doc ID
}

const adjustments: AdjustmentType[] = [
    { id: "clock_in", type: "Clock In" },
    { id: "clock_out", type: "Clock Out" },
    { id: "break_start", type: "Break Start" },
    { id: "break_end", type: "Break End" },
]

type RequestGroup = {
    userId: string;
    name: string;
    requests: AdjustmentRequestWithId[];
};

const getPunchVariant = (entryType: string) => {
    if (entryType === "clock_in") return "in";
    if (entryType === "clock_out") return "out";
    return "break";
};

const formatClock = (timestamp: Timestamp) =>
    timestamp.toDate().toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

// Shows the weekday too when a punch lands on a different day than the request (overnight shifts).
const formatPunchTime = (timestamp: Timestamp, date: string) =>
    dayjs(timestamp.toDate()).format("YYYY-MM-DD") === date
        ? formatClock(timestamp)
        : `${dayjs(timestamp.toDate()).format("ddd")} ${formatClock(timestamp)}`;

const formatTimeChange = (original: Timestamp, updated: Timestamp) => {
    const minutes = Math.round((updated.toMillis() - original.toMillis()) / 60000);
    if (minutes === 0) return "same time";
    const hours = Math.floor(Math.abs(minutes) / 60);
    const remainder = Math.abs(minutes) % 60;
    const amount = [hours ? `${hours} hr` : "", remainder ? `${remainder} min` : ""].filter(Boolean).join(" ");
    return `${amount} ${minutes > 0 ? "later" : "earlier"}`;
};

const formatHours = (minutes: number) => (minutes / 60).toFixed(2);

const getPtoDayCount = (startDate: string, endDate: string) => dayjs(endDate).diff(dayjs(startDate), "day") + 1;

const formatPtoRange = (startDate: string, endDate: string) => startDate === endDate
    ? dayjs(startDate).format("ddd, MMM D, YYYY")
    : `${dayjs(startDate).format("MMM D")} – ${dayjs(endDate).format("MMM D, YYYY")}`;

interface TimeManagerProps {
    employeeDetailPage: boolean;
}

/* 
    This component should likely be split up into multple files...
    The idea here is to display all of a users time entries with the ability to add new entries to their schedule
    or edit existing entries to their schedule. IF they are not admin users it puts in a request for the time change
     For admin users there is a lisst of time change requests that they need to approve or decline. This component is used for the time
     manager page as well as the payroll page as the display is the same where it shows the given users time entries. On the payroll page 
     it will allow an admin user to adjust anyones schedule. 

     There is also a pto tab with all the same functions. There is a form to submit pto and an area where admin users must approve or decline
     the pto requests
*/
export const TimeManager: React.FC<TimeManagerProps> = ({ employeeDetailPage }) => {
    const { userData } = useUser();
    const [userTimeEntryMap, setUserTimeEntryMap] = React.useState<Record<string, { events: TimeEvent[]; minutesWorked: number }>>({});
    const [adjustmentDate, setAdjustmentDate] = useState<string>("");
    const [adjustmentTime, setAdjustmentTime] = useState<string>("");
    const [adjustmentType, setAdjustmentType] = useState<string>("");
    const [ptoStartDate, setPtoStart] = useState<string>("");
    const [ptoEndDate, setPtoEnd] = useState<string>("");
    const [editEntry, setEditEntry] = useState<TimeEvent | null>(null)
    const [entryDialogOpen, setEntryDialogOpen] = useState(false);
    const [isSubmittingEntry, setIsSubmittingEntry] = useState(false);
    const [entriesVersion, setEntriesVersion] = useState(0);
    const [busyApprovalKey, setBusyApprovalKey] = useState<string | null>(null);
    const [myPendingRequests, setMyPendingRequests] = useState<AdjustmentRequestWithId[] | null>(null);
    const [message, setMessage] = useState<string>("");
    const { dateRange, setDateRange } = useDateRange();
    const [pendingRequests, setPendingRequests] = useState<AdjustmentRequestWithId[] | null>(null);
    const [pendingPtoRequests, setPendingPtoRequests] = useState<PtoRequestWithId[] | null>(null);
    const [myPtoRequests, setMyPtoRequests] = useState<PtoRequestWithId[] | null>(null);
    const [dialogOpen, setDialogOpen] = useState<boolean>(false);
    const [ptoDaysDialog, setOpenPtoDaysDialog] = useState<boolean>(false);
    const [dialogTitle, setDialogTitle] = useState("");
    const [dialogMessage, setDialogMessage] = useState("");
    const [removeEntry, setReomveEntry] = useState(false)
    const [employee, setEmployee] = useState<Employee | null>(null);
    const [tabIndex, setTabIndex] = useState(0);
    const [blackoutDates, setBlackoutDates] = useState<string[]>([]);

    const blackoutDateSet = useMemo(() => new Set(blackoutDates), [blackoutDates]);
    const [totalHoursTaken, setTotalHoursTaken] = useState(0);
    const [totalPtoAllowed, setTotalPtoAllowed] = useState(0);
    const { employeeId: paramEmployeeId } = useParams<{ employeeId?: string }>();
    const [newPtoRequest, setNewPtoRequest] = useState<PtoRequestWithId | null>(null);
    const [approvalPtoHoursPerDay, setApprovalPtoHoursPerDay] = useState(0);



    //getting the id either from the url or the user thats logged in. 
    //if we have the url id then we want to use that otherwise we want to use the userDataId
    const employeeId = useMemo(() => {
        return paramEmployeeId ?? userData?.id ?? null;
    }, [paramEmployeeId, userData?.id]);

    //if we are on the employee detail page then we want to get their info
    useEffect(() => {

        const fetchEmployee = async () => {
            if (!paramEmployeeId) {
                setEmployee(null)
                return;
            }

            const docRef = doc(db, "employees", paramEmployeeId);
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {

                const empData = docSnap.data() as Employee
                console.log("emp data", empData)
                setEmployee(empData);
                // pto allowed is equal to the accrued hours / by the pto ratio. 

                //we have a limit of -12 for a difference of days taken and days allowed to 
                //accomamdate for early in teh year pto where people haven't built up their hours yet
                var hoursWorked = 0
                if (empData.accruedMinutesYTD) hoursWorked = empData.accruedMinutesYTD / 60

                const hoursAllowed = hoursWorked * empData.ptoRatio



                setTotalPtoAllowed(hoursAllowed)
            }
        }

        fetchEmployee();
    }, [paramEmployeeId]);

    useEffect(() => {
        const fetchBlackoutDates = async () => {
            try {
                const snapshot = await getDocs(collection(db, "blackout_days"));
                const dates = snapshot.docs.map((docSnapshot) => {
                    const data = docSnapshot.data() as { date?: string };
                    return data.date || docSnapshot.id;
                });
                dates.sort();
                setBlackoutDates(dates);
            } catch (error) {
                console.error("Error fetching blackout dates:", error);
            }
        };

        fetchBlackoutDates();
    }, []);

    //getting my pto requests
    useEffect(() => {
        if (userData && userData.id) {
            const fetchMyPtoRequests = async () => {
                try {
                    //only getting pto from the current year for display purposes
                    const currentYear = new Date().getFullYear();
                    const startOfYear = `${currentYear}-01-01`;
                    const startOfNextYear = `${currentYear + 1}-01-01`;
                    const q = query(
                        collection(db, "pto_requests"),
                        where("userId", "==", userData?.id),
                        where("startDate", ">=", startOfYear),
                        where("startDate", "<", startOfNextYear)
                    );

                    const querySnapshot = await getDocs(q);
                    const results = querySnapshot.docs.map(doc => ({
                        id: doc.id,
                        ...doc.data(),
                    })) as PtoRequestWithId[];

                    setMyPtoRequests(results);

                    const approvedRequests = results.filter(req => req.status?.toLowerCase() === "approved");

                    let totalApprovedHours = 0
                    approvedRequests.forEach(req => {
                        totalApprovedHours += typeof req.approvedHours === "number"
                            ? req.approvedHours
                            : req.approvedDays * userData.ptoTakenHoursPerDay

                    });


                    let totalHoursTaken = totalApprovedHours

                    setTotalHoursTaken(totalHoursTaken)

                    var hoursWorked = 0
                    if (userData.accruedMinutesYTD) hoursWorked = userData.accruedMinutesYTD / 60

                    const hoursAllowed = hoursWorked * userData.ptoRatio


                    setTotalPtoAllowed(hoursAllowed)


                } catch (error) {
                    console.error("Error fetching PTO requests:", error);
                }
            };

            fetchMyPtoRequests();
        }

    }, [userData?.id]);

    //fetching the time entries to display all the events
    useEffect(() => {

        if (!dateRange.startDate || !dateRange.endDate) return;
        //ignore results from older requests (e.g. while a date is being typed) so they can't overwrite newer ones
        let isCurrent = true;
        const fetchData = async () => {
            if (employeeId && dateRange.startDate && dateRange.endDate) {
                const newTimeEntryMap: Record<
                    string,
                    { events: TimeEvent[]; minutesWorked: number }
                > = {};

                const start = dateRange.startDate?.format("YYYY-MM-DD");
                const end = dateRange.endDate?.add(1, "day").format("YYYY-MM-DD"); //plus one to check for overnights
                const trueEnd = dateRange.endDate?.format("YYYY-MM-DD"); //plus one to check for overnights
                const q = query(
                    collection(db, "time_entries"),
                    where("userId", "==", employeeId),
                    where("date", ">=", start),
                    where("date", "<=", end)
                );

                const snapshot = await getDocs(q);

                // collect all events for this user
                let allEvents: TimeEvent[] = [];

                snapshot.docs.forEach((doc) => {
                    const data = doc.data();
                    const date = data.date;
                    let events: TimeEvent[] = Array.isArray(data.events) ? data.events : [];

                    // Sort by timestamp
                    events = events.sort((a, b) => {
                        const aTime = a.timestamp?.toMillis?.() ?? 0;
                        const bTime = b.timestamp?.toMillis?.() ?? 0;
                        return aTime - bTime;
                    });

                    allEvents.push(...events);

                    // Initialize with 0, will update below
                    if (date >= start && date <= trueEnd) { //this is to make sure we only add dates in the selected range not the additional day we look at for overnights
                        newTimeEntryMap[date] = { events, minutesWorked: 0 };
                    }
                });

                // Run your overnight/daily breakdown
                const minutesByDay = calculateWorkedMinutesWithOvernights(allEvents);

                // Merge results back into map
                for (const [date, entry] of Object.entries(newTimeEntryMap)) {
                    newTimeEntryMap[date] = {
                        ...entry,
                        minutesWorked: minutesByDay[date] ?? 0, // fallback 0 if not present
                    };
                }

                if (isCurrent) setUserTimeEntryMap(newTimeEntryMap);
            }
        };


        fetchData();
        return () => { isCurrent = false; };
    }, [employeeId, dateRange.startDate, dateRange.endDate, entriesVersion]);

    //non-admins see their own requests that are still waiting on an admin
    useEffect(() => {
        if (!userData?.id || userData.admin || paramEmployeeId) return;

        const fetchMyPendingRequests = async () => {
            try {
                const q = query(
                    collection(db, "adjustment_requests"),
                    where("userId", "==", userData.id),
                    where("status", "==", "pending")
                );
                const querySnapshot = await getDocs(q);
                const results = querySnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data(),
                })) as AdjustmentRequestWithId[];
                results.sort((first, second) => first.updated.timestamp.toMillis() - second.updated.timestamp.toMillis());
                setMyPendingRequests(results);
            } catch (error) {
                console.error("Error fetching my adjustment requests:", error);
            }
        };

        fetchMyPendingRequests();
    }, [userData?.id, userData?.admin, paramEmployeeId]);


    //fetching the pending schedule requests
    useEffect(() => {
        if (!userData?.admin) return;

        const fetchPendingRequests = async () => {
            try {
                const q = query(
                    collection(db, "adjustment_requests"),
                    where("status", "==", "pending")
                );
                const querySnapshot = await getDocs(q);
                const results = querySnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data(),
                })) as AdjustmentRequestWithId[];

                setPendingRequests(results);
            } catch (error) {
                console.error("Error fetching adjustment requests:", error);
            }
        };

        fetchPendingRequests();

        const fetchPendingPtoRequests = async () => {
            try {
                const q = query(
                    collection(db, "pto_requests"),
                    where("status", "==", "Pending")
                );
                const querySnapshot = await getDocs(q);
                const results = querySnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data(),
                })) as PtoRequestWithId[];

                setPendingPtoRequests(sortPtoRequestsByRequestedAt(results));
            } catch (error) {
                console.error("Error fetching adjustment requests:", error);
            }
        };

        fetchPendingPtoRequests();

    }, [userData?.admin]);

    const handleTabChange = (_event: React.SyntheticEvent, newIndex: number) => {
        setTabIndex(newIndex);
    };

    const sortPtoRequestsByRequestedAt = (requests: PtoRequestWithId[]) => {
        return requests.sort((a, b) => {
            const aRequestedAt = a.requestedAt?.toMillis() ?? Number.POSITIVE_INFINITY;
            const bRequestedAt = b.requestedAt?.toMillis() ?? Number.POSITIVE_INFINITY;

            if (aRequestedAt !== bRequestedAt) {
                return aRequestedAt - bRequestedAt;
            }

            return a.startDate.localeCompare(b.startDate);
        });
    };

    //ui display function
    const formatEntryType = (entryType: string) => {
        if (entryType === "clock_in") return "Clock In"
        if (entryType === "clock_out") return "Clock Out"
        if (entryType === "break_start") return "Break Start"
        if (entryType === "break_end") return "Break End"
        return ""
    }

    //
    const getDocRef = () => {
        //double checking here that the employeeId makes sense based on what page we are on
        //if the paramEmployeeId exists and its not the same as the employeeId then we have issues
        if (paramEmployeeId !== undefined && paramEmployeeId !== employeeId) {
            setMessage("Something went wrong, please refresh your page and try again")
            return
        }

        const docId = `${employeeId}_${adjustmentDate}`;
        return doc(db, "time_entries", docId);

    }

    const handleSubmitForm = async () => {
        //the entry dialog is the confirmation step, so validate and submit directly
        //we are not editing so lets make sure all the fields are filled out
        if (editEntry === null) {
            if (adjustmentType === "") {
                setMessage("Please Select an Adjustment Type");
                return;
            }
            if (adjustmentDate === "") {
                setMessage("Please Select a Date");
                return;
            }
            if (adjustmentTime === "") {
                setMessage("Please Select a Time");
                return;
            }
        }
        if (adjustmentTime === "") {
            setMessage("Please Select a Time");
            return;
        }

        setIsSubmittingEntry(true);
        try {
            await handleConfirmSubmission();
        } finally {
            setIsSubmittingEntry(false);
        }
    }

    const resetState = () => {
        setEditEntry(null)
        setAdjustmentType("");
        setAdjustmentTime("");
        setAdjustmentDate("");
        setDialogOpen(false);
        setEntryDialogOpen(false);
        setReomveEntry(false)
    }

    //checks if the form was an edit or a add new event and calls the appropriate functions
    const handleConfirmSubmission = async () => {
        if (removeEntry) {
            await confirmRemoveEntry()
            return
        }
        if (editEntry !== null) {
            //we are editing so lets get an edit ready

            if (userData) {
                //if they are an admin then just submit the entry
                if (userData.admin) {
                    //idea here is to get the doucment. compare the entries to the our update entry
                    //replae the entries array and update the whole doc since firestore doesn't support 
                    //updating an array in place
                    const docRef = getDocRef()
                    if (docRef) {
                        const docSnap = await getDoc(docRef);
                        if (!docSnap.exists()) {
                            console.error("Document does not exist.");
                            return;
                        }
                        const data = docSnap.data();
                        const events = data.events
                        const originalEntry = editEntry
                        const newTimestamp = generateFirestoreTimestampFromString(adjustmentDate, adjustmentTime)

                        const updatedEvents = events.map((event: TimeEvent) => {
                            const isSameEvent =
                                event.type === originalEntry.type &&
                                event.timestamp?.seconds === originalEntry.timestamp?.seconds;

                            return isSameEvent
                                ? { ...event, timestamp: newTimestamp }
                                : event;
                        });

                        try {
                            if (!employeeId) {
                                setMessage("Missing employee id.");
                                return;
                            }

                            await updateAccruedMinutesForDate(db, employeeId, adjustmentDate, async () => {
                                await updateDoc(docRef, { events: updatedEvents });
                            });
                            addTimeEntry({ type: adjustmentType, timestamp: newTimestamp }, editEntry.timestamp);
                            setMessage("Adjustment Successful");
                            resetState();
                        } catch (error) {
                            setDialogOpen(false);
                            setMessage("Adjustment Failed, Please Try Again");

                        }
                    }



                } else {
                    //otherwise submit for approval
                    const original = editEntry
                    const firestoreTimestamp = generateFirestoreTimestampFromString(adjustmentDate, adjustmentTime)

                    const updated: TimeEvent = {
                        type: adjustmentType,
                        timestamp: firestoreTimestamp,
                    };

                    const requestData = {
                        userId: employeeId,
                        firstName: userData.firstName,
                        lastName: userData.lastName,
                        date: adjustmentDate,
                        original,
                        updated,
                        status: "pending",
                        requestedAt: Timestamp.now(),
                        reviewedBy: null,
                        reviewedAt: null
                    };

                    try {
                        const requestRef = await addDoc(collection(db, "adjustment_requests"), requestData);
                        setMyPendingRequests((current) => [...(current ?? []), { id: requestRef.id, ...requestData } as unknown as AdjustmentRequestWithId]);
                        setMessage("Adjustment Sent For Approval");
                        resetState();
                    } catch (error) {
                        setDialogOpen(false);
                        console.error("Error updating document:", error);
                        setMessage("Failed to adjust time. Please try again.");
                    }

                }

            }
        } else {
            //we are adding a new entry 
            await addNewEntry()
        }
    }

    //pretty selfexplanitory name here
    const generateFirestoreTimestampFromString = (adjustmentDate: string, adjustmentTime: string) => {
        // Create a local Date object based on date and time
        const [hourStr, minuteStr] = adjustmentTime.split(":");
        const year = Number(adjustmentDate.slice(0, 4));
        const month = Number(adjustmentDate.slice(5, 7)) - 1; // Months are 0-based in JS Date
        const day = Number(adjustmentDate.slice(8, 10));
        const hour = Number(hourStr);
        const minute = Number(minuteStr);

        // Create the Date object in local time (not UTC)
        const localDate = new Date(year, month, day, hour, minute);

        // Convert to Firestore Timestamp
        const firestoreTimestamp = Timestamp.fromDate(localDate);
        return firestoreTimestamp
    }

    //adds a new entry to time_entries if you are an admin or submits a request
    const addNewEntry = async () => {
        if (userData) {
            if (userData.admin) {
                //if they are an admin then we don't need to submit a request just edit it
                const docRef = getDocRef();

                if (docRef) {
                    const firestoreTimestamp = generateFirestoreTimestampFromString(adjustmentDate, adjustmentTime)
                    const newEvent = { type: adjustmentType, timestamp: firestoreTimestamp };
                    try {
                        if (!employeeId) {
                            setMessage("Missing employee id.");
                            return;
                        }

                        await updateAccruedMinutesForDate(db, employeeId, adjustmentDate, async () => {
                            const docSnap = await getDoc(docRef);

                            if (docSnap.exists()) {
                                // Document exists → update with new event
                                await updateDoc(docRef, {
                                    events: arrayUnion(newEvent),
                                });
                            } else {
                                // Document does NOT exist → create it with this event
                                await setDoc(docRef, {
                                    userId: employeeId,
                                    date: adjustmentDate,
                                    events: [newEvent],
                                });
                            }
                        });

                        setMessage("Adjustment Successful");
                        resetState();
                        addTimeEntry(newEvent, null);
                        setEditEntry(null);

                    } catch (error) {
                        console.error("Error updating/creating document:", error);
                        setMessage("Failed to adjust time. Please try again.");
                    }
                }


            } else {

                const firestoreTimestamp = generateFirestoreTimestampFromString(adjustmentDate, adjustmentTime)

                const updated: TimeEvent = {
                    type: adjustmentType,
                    timestamp: firestoreTimestamp,
                };

                const requestData = {
                    userId: employeeId,
                    date: adjustmentDate,
                    firstName: userData.firstName,
                    lastName: userData.lastName,
                    original: null,//this is a new entry so there is no original
                    updated,
                    status: "pending",
                    requestedAt: Timestamp.now(),
                    reviewedBy: null,
                    reviewedAt: null
                };

                //make the actyal call
                try {
                    const requestRef = await addDoc(collection(db, "adjustment_requests"), requestData);
                    setMyPendingRequests((current) => [...(current ?? []), { id: requestRef.id, ...requestData } as unknown as AdjustmentRequestWithId]);
                    setMessage("Adjustment Sent For Approval");
                    resetState();
                } catch (error) {
                    console.error("Error updating document:", error);
                    setMessage("Failed to submit time. Please try again.");
                }

            }
        }

    }

    //closes the snackbar and updates message
    const handleClose = () => {
        setMessage("")
    }

    //this adds or edits the time entries state variable to display any new edits or additions
    //it has some bad logic because im dumb
    const addTimeEntry = (newEntry: { type: string; timestamp: Timestamp }, originalTime: Timestamp | null) => {
        //thiis is a little silly but im a silly guy
        if (originalTime === null) {
            setUserTimeEntryMap((prevMap) => {
                const updatedMap = { ...prevMap };

                if (!updatedMap[adjustmentDate]) {
                    updatedMap[adjustmentDate] = { events: [], minutesWorked: 0 };
                }

                updatedMap[adjustmentDate].events = [...updatedMap[adjustmentDate].events, newEntry];

                return updatedMap;
            });

        } else {
            setUserTimeEntryMap((prevMap) => {
                const updatedMap = { ...prevMap };
                const existingEntries = updatedMap[adjustmentDate] ?? [];

                const updatedEntries = existingEntries.events.map((entry) => {
                    // Compare timestamps using isEqual
                    if (entry.timestamp instanceof Timestamp && entry.timestamp.isEqual(originalTime)) {
                        return newEntry; // Replace with the new one
                    }
                    return entry; // Keep the old one
                });

                updatedMap[adjustmentDate].events = updatedEntries;
                return updatedMap;
            });
        }


    };

    //ya
    const getTimeZoneStringFromDate = (convertDate: Date): string => {
        const now = convertDate
        const options: Intl.DateTimeFormatOptions = {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            timeZone: 'America/Denver', // MST/MDT (with DST handling)
        };

        // format: MM/DD/YYYY
        const formatter = new Intl.DateTimeFormat('en-US', options);
        const parts = formatter.formatToParts(now);
        const year = parts.find(p => p.type === 'year')?.value;
        const month = parts.find(p => p.type === 'month')?.value;
        const day = parts.find(p => p.type === 'day')?.value;
        const today = `${year}-${month}-${day}`; // "YYYY-MM-DD" format
        return today
    }

    const handleRemoveEntry = (entry: TimeEvent) => {
        const adjDate = getTimeZoneStringFromDate(entry.timestamp.toDate())
        setAdjustmentDate(adjDate)

        setEditEntry(entry)
        setDialogOpen(true);
        setDialogTitle("Confirm Remove Time Entry")
        setDialogMessage("Are you sure you want to remove this time entry from your time clock?")
        setReomveEntry(true)
    }

    const confirmRemoveEntry = async () => {
        const docRef = getDocRef();

        if (docRef && editEntry) {
            const docSnap = await getDoc(docRef);
            if (!docSnap.exists()) {
                console.error("Document does not exist.");
                return;
            }

            const data = docSnap.data();
            const events = data.events;

            const originalEntry = editEntry;

            // Filter out the event that exactly matches the one we're removing
            const updatedEvents = events.filter((event: TimeEvent) => {
                return !(
                    event.type === originalEntry.type &&
                    event.timestamp?.seconds === originalEntry.timestamp?.seconds
                );
            });

            try {
                if (!employeeId) {
                    setMessage("Missing employee id.");
                    return;
                }

                await updateAccruedMinutesForDate(db, employeeId, adjustmentDate, async () => {
                    if (updatedEvents.length === 0) {
                        // No more events left — delete the entire document
                        await deleteDoc(docRef);
                    } else {
                        // Update the document with the remaining events
                        await updateDoc(docRef, { events: updatedEvents });
                    }
                });


                // Update UI if needed
                // removeTimeEntry(originalEntry); // Assuming you have a function like this
                setMessage("Entry removed successfully");
                setUserTimeEntryMap((prevMap) => {
                    const updatedMap = { ...prevMap };
                    const existingEntryGroup = updatedMap[adjustmentDate];

                    if (!existingEntryGroup) return prevMap; // Nothing to update

                    const filteredEntries = existingEntryGroup.events.filter((entry) => {
                        // Remove the entry that matches the timestamp
                        return !(entry.timestamp instanceof Timestamp && entry.timestamp.isEqual(editEntry.timestamp));
                    });

                    updatedMap[adjustmentDate] = {
                        ...existingEntryGroup,
                        events: filteredEntries,
                    };

                    return updatedMap;
                });
                resetState();
            } catch (error) {
                console.error("Failed to remove entry:", error);
                setDialogOpen(false);
                setMessage("Failed to remove entry. Please try again.");
            }
        }

        resetState();
    };


    //when you click edit on the entry it calls this and upddates the form and state
    const handleEditEntry = (entry: TimeEvent) => {
        setEditEntry(entry)
        setAdjustmentType(entry.type)
        const adjTime = entry.timestamp.toDate().toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
        })
        const adjDate = getTimeZoneStringFromDate(entry.timestamp.toDate())
        setAdjustmentTime(adjTime)
        setAdjustmentDate(adjDate)
        setEntryDialogOpen(true)
    }

    //this clears the form because they want to add a new entry, optionally starting on a given day
    const handleAddNewEntry = (date = "") => {
        setAdjustmentType("")
        setAdjustmentTime("")
        setAdjustmentDate(date)
        setEditEntry(null)
        setEntryDialogOpen(true)
    }

    const handleDeclineTimeRequest = async (request: AdjustmentRequestWithId) => {
        try {

            // Mark adjustment as approved
            await updateDoc(doc(db, "adjustment_requests", request.id), {
                status: "declined",
                reviewedBy: userData?.id,
                reviewedAt: Timestamp.now()
            });

            setMessage("Adjustment Declined");
            setEditEntry(null);

            setPendingRequests((current) => current?.filter(r => r.id !== request.id) ?? current);
        } catch (error) {
            console.error("Failed to create time entry document:", error);
            setMessage("Adjustment Failed, Please Try Again");
        }
    }

    const handleApproveRequest = async (request: AdjustmentRequestWithId): Promise<boolean> => {

        //this will error if they don't have an events for that given day. Need to create a document in 
        //that case

        const docId = `${request?.userId}_${request.date}`;
        const docRef = doc(db, "time_entries", docId);
        try {
            await updateAccruedMinutesForDate(db, request.userId, request.date, async () => {
                const docSnap = await getDoc(docRef);

                if (!docSnap.exists()) {
                    // Create a new document for the time entry since one doesnt exist yet
                    const newDocData = {
                        userId: request.userId,
                        date: request.date,
                        events: [request.updated] // Start with the new entry
                    };
                    await setDoc(docRef, newDocData);
                    return;
                }

                //these are the existing events of the existing document in the given users database
                const data = docSnap.data();
                const events = data.events || [];

                const originalEntry = request.original;
                const newEntry = request.updated;

                let updatedEvents;

                if (originalEntry === null) {
                    // Adding a completely new event
                    updatedEvents = [...events, newEntry];
                } else {
                    // Editing an existing event
                    updatedEvents = events.map((event: TimeEvent) => {
                        const isSameEvent =
                            event.type === originalEntry.type &&
                            event.timestamp?.seconds === originalEntry.timestamp?.seconds;

                        return isSameEvent
                            ? { ...event, timestamp: newEntry.timestamp }
                            : event;
                    });
                }

                //this udpates the actual document to contain the new time entry
                await updateDoc(docRef, { events: updatedEvents });
            });

            await updateDoc(doc(db, "adjustment_requests", request.id), {
                status: "approved",
                reviewedBy: userData?.id,
                reviewedAt: Timestamp.now()
            });

            setMessage("Adjustment Successful");
            setEditEntry(null);

            setPendingRequests((current) => current?.filter(r => r.id !== request.id) ?? current);
            //refresh the entries on screen if this request changed them
            if (request.userId === employeeId) setEntriesVersion((version) => version + 1);
            return true;
        } catch (error) {
            console.error(error);
            setMessage("Adjustment Failed, Please Try Again");
            return false;
        }
    };

    const runApprovalAction = async (key: string, action: () => Promise<unknown>) => {
        setBusyApprovalKey(key);
        try {
            await action();
        } finally {
            setBusyApprovalKey(null);
        }
    };

    //approves every pending time adjustment for one employee, one at a time
    const handleApproveAll = (group: RequestGroup) => {
        if (!window.confirm(`Approve all ${group.requests.length} time adjustments for ${group.name}?`)) return;

        runApprovalAction(`user:${group.userId}`, async () => {
            let approvedCount = 0;
            for (const request of group.requests) {
                if (await handleApproveRequest(request)) approvedCount += 1;
            }
            setMessage(approvedCount === group.requests.length
                ? `Approved ${approvedCount} adjustments for ${group.name}`
                : `Approved ${approvedCount} of ${group.requests.length}. Please try the rest again.`);
        });
    };

    const formatShortDate = (input: string) => {
        const [year, month, day] = input.split("-");
        return `${month}-${day}-${year}`;
    };

    const handleSubmitPtoRequest = async () => {

        //submit an enrty to the pto_requests collection with requestingId: userdata.id, ptoStartDate, ptoEndDate
        //status, reviewedBy, reviewedAt
        if (userData) {
            if (!ptoStartDate || !ptoEndDate) {
                setMessage("Select PTO start and end dates.");
                return;
            }

            const start = dayjs(ptoStartDate);
            const end = dayjs(ptoEndDate);

            if (end.isBefore(start, "day")) {
                setMessage("End date cannot be before start date.");
                return;
            }

            const blockedDates: string[] = [];
            let current = start;
            while (!current.isAfter(end, "day")) {
                const dateStr = current.format("YYYY-MM-DD");
                if (blackoutDateSet.has(dateStr)) {
                    blockedDates.push(dateStr);
                }
                current = current.add(1, "day");
            }

            if (blockedDates.length > 0) {
                const readableDates = blockedDates.map(formatShortDate).join(", ");
                setMessage(`PTO request includes blackout date${blockedDates.length > 1 ? "s" : ""}: ${readableDates}`);
                return;
            }

            const requestData = {
                userId: userData?.id,
                startDate: ptoStartDate,
                endDate: ptoEndDate,
                firstName: userData.firstName,
                lastName: userData.lastName,

                status: "Pending",
                requestedAt: Timestamp.now(),
                reviewedBy: null,
                reviewedAt: null,
                ptoUsed: totalHoursTaken,
            };

            //make the actyal call
            try {
                const requestRef = await addDoc(collection(db, "pto_requests"), requestData);
                setMyPtoRequests((current) => [...(current ?? []), { id: requestRef.id, ...requestData } as unknown as PtoRequestWithId]);
                setPtoStart("");
                setPtoEnd("");
                setMessage("PTO Request Sent For Approval");
                resetState();
            } catch (error) {
                console.error("Error updating document:", error);
                setMessage("Failed to submit pto. Please try again.");
            }
        }

    }

    const formatRequestedAt = (requestedAt?: Timestamp) => {
        if (!requestedAt) return "Not available";

        return dayjs(requestedAt.toDate()).format("MMM D, h:mm A");
    }

    //this function needs to first pop open a dialog that prompts the admin user
    //to enter how many pto days this should count as. then it should submit this data with 
    //approvedDays as that value
    const handleApprovePto = async (request: PtoRequestWithId) => {
        //set state function to save request
        setNewPtoRequest(request)
        setApprovalPtoHoursPerDay(0)

        try {
            const requestUserSnap = await getDoc(doc(db, "employees", request.userId));
            const requestUser = requestUserSnap.exists()
                ? (requestUserSnap.data() as Employee)
                : null;
            setApprovalPtoHoursPerDay(requestUser?.ptoTakenHoursPerDay ?? userData?.ptoTakenHoursPerDay ?? 0);
        } catch (error) {
            console.error("Error fetching PTO approval employee:", error);
            setApprovalPtoHoursPerDay(userData?.ptoTakenHoursPerDay ?? 0);
        }

        //open dialog
        setOpenPtoDaysDialog(true)
    }

    const handleFinalizePto = async (approvedDays: number, approvedHours: number) => {
        let request = newPtoRequest;

        if (!request) return;

        const docRef = doc(db, "pto_requests", request.id);

        try {
            const requestUserRef = doc(db, "employees", request.userId);
            const requestUserSnap = await getDoc(requestUserRef);
            const requestUser = requestUserSnap.exists()
                ? (requestUserSnap.data() as Employee)
                : null;
            const requestUserHoursPerDay =
                requestUser?.ptoTakenHoursPerDay ?? userData?.ptoTakenHoursPerDay ?? 0;

            // 1. Update the approved request
            await updateDoc(docRef, {
                status: "approved",
                reviewedBy: userData?.id,
                reviewedAt: Timestamp.now(),
                approvedDays: approvedDays,
                approvedHours: approvedHours,
            });

            // 2. Query all other PTO requests for the same user
            const q = query(
                collection(db, "pto_requests"),
                where("userId", "==", request.userId)
            );

            const snapshot = await getDocs(q);

            let totalApprovedHours = 0;
            snapshot.forEach((docSnap) => {
                const data = docSnap.data() as PtoRequestWithId;
                if (data.status?.toLowerCase() === "approved") {
                    totalApprovedHours += typeof data.approvedHours === "number"
                        ? data.approvedHours
                        : Number(data.approvedDays ?? 0) * requestUserHoursPerDay;
                }
            });

            const computedPtoUsed = totalApprovedHours;

            console.log("[PTO ADMIN] requestUserId:", request.userId);
            console.log("[PTO ADMIN] requestUserHoursPerDay:", requestUserHoursPerDay);
            console.log("[PTO ADMIN] totalApprovedHours (request user):", totalApprovedHours);
            console.log("[PTO ADMIN] computedPtoUsed (request user):", computedPtoUsed);

            // 3. Batch update PTO used on all requests for this user
            const batch = writeBatch(db);

            snapshot.forEach((docSnap) => {
                batch.update(docSnap.ref, {
                    ptoUsed: computedPtoUsed,
                });
            });

            await batch.commit();

            setMessage("Approved PTO, Have Fun!");
            resetState();

            //remove the request we just approved and update pto used on this user's other requests
            setPendingPtoRequests((current) => current
                ? sortPtoRequestsByRequestedAt(current
                    .filter((r) => r.id !== request?.id)
                    .map((req) => req.userId === request?.userId ? { ...req, ptoUsed: computedPtoUsed } : req))
                : current);

            // 4. Update schedules
            await addPtoToSchedules(
                request.userId,
                dayjs(request.startDate).toDate(),
                dayjs(request.endDate).toDate()
            );

            setOpenPtoDaysDialog(false);
        } catch (error) {
            console.error(error);
            setMessage("Adjustment Failed, Please Try Again");
        }
    };


    const addPtoToSchedules = async (
        userId: string,
        startDate: Date,
        endDate: Date
    ) => {
        const start = dayjs(startDate);
        const end = dayjs(endDate);
        const promises = [];

        for (let date = start; date.isSameOrBefore(end); date = date.add(1, "day")) {
            const formattedDate = date.format("YYYY-MM-DD");

            const shiftStart = Timestamp.fromDate(new Date(`${formattedDate}T00:00:00`));
            const shiftEnd = Timestamp.fromDate(new Date(`${formattedDate}T23:59:59`));

            const scheduleDocRef = doc(db, "schedules", `${userId}_${formattedDate}`);
            const scheduleData = {
                userId,
                date: formattedDate,
                location: "Pto",
                shiftStart,
                shiftEnd,
            };

            promises.push(setDoc(scheduleDocRef, scheduleData));
        }

        try {
            await Promise.all(promises);
            console.log("Time off schedule created for all dates.");
        } catch (error) {
            console.error("Error creating time off schedule:", error);
            throw error;
        }
    };


    const handleDeclinePto = async (request: PtoRequestWithId) => {
        const docRef = doc(db, "pto_requests", request.id);
        try {
            //this udpates the actual document to contain the new time entry
            await updateDoc(docRef, {
                status: "declined",
                reviewedBy: userData?.id,
                reviewedAt: Timestamp.now()
            });



            setMessage("Declined Pto, Sorry...");
            resetState();
            setPendingPtoRequests((current) => current?.filter(r => r.id !== request.id) ?? current);
        } catch (error) {
            console.error(error);
            setMessage("Adjustment Failed, Please Try Again");
        }
    }

    const sortedEntryDays = Object.entries(userTimeEntryMap).sort(([firstDate], [secondDate]) => firstDate.localeCompare(secondDate));
    const totalMinutesWorked = sortedEntryDays.reduce((total, [, day]) => total + day.minutesWorked, 0);
    const daysWorked = sortedEntryDays.filter(([, day]) => day.minutesWorked > 0).length;
    const daysWithMissingPunch = sortedEntryDays.filter(([, day]) => day.events.length % 2 === 1).length;

    const showApprovals = Boolean(userData?.admin) && !employeeDetailPage;
    const pendingTimeCount = pendingRequests?.length ?? 0;
    const pendingPtoCount = pendingPtoRequests?.length ?? 0;
    const pendingTotal = pendingTimeCount + pendingPtoCount;

    const pendingRequestGroups: RequestGroup[] = Object.values(
        (pendingRequests ?? []).reduce<Record<string, RequestGroup>>((groups, request) => {
            if (!groups[request.userId]) {
                groups[request.userId] = { userId: request.userId, name: `${request.firstName} ${request.lastName}`, requests: [] };
            }
            groups[request.userId].requests.push(request);
            return groups;
        }, {})
    )
        .map((group) => ({
            ...group,
            requests: [...group.requests].sort((first, second) => first.updated.timestamp.toMillis() - second.updated.timestamp.toMillis()),
        }))
        .sort((first, second) => first.name.localeCompare(second.name));

    const sortedMyPtoRequests = [...(myPtoRequests ?? [])].sort((first, second) => second.startDate.localeCompare(first.startDate));
    const today = dayjs().format("YYYY-MM-DD");
    const upcomingBlackoutDates = blackoutDates.filter((date) => date >= today).slice(0, 6);
    const ptoRequestDays = ptoStartDate && ptoEndDate && ptoEndDate >= ptoStartDate ? getPtoDayCount(ptoStartDate, ptoEndDate) : 0;

    const pageTitle = tabIndex === 1 ? "Paid time off"
        : tabIndex === 2 ? "Approvals"
            : employee?.firstName ? `${employee.firstName} ${employee.lastName}` : "My time";

    const renderRequestChange = (request: AdjustmentRequestWithId) => request.original ? (
        <span className="tm-change">
            <s>{formatPunchTime(request.original.timestamp, request.date)}</s>
            <span aria-hidden="true">→</span>
            <strong>{formatPunchTime(request.updated.timestamp, request.date)}</strong>
            <em>{formatTimeChange(request.original.timestamp, request.updated.timestamp)}</em>
        </span>
    ) : (
        <span className="tm-change"><strong>{formatPunchTime(request.updated.timestamp, request.date)}</strong><em>new punch</em></span>
    );

    return (
        <div className="time-manager-page">
            <Header />

            {employeeDetailPage ? null :
                <div className="tm-tabs">
                    <Tabs value={tabIndex} onChange={handleTabChange} className="tm-tab-list" TabIndicatorProps={{ style: { backgroundColor: 'black' } }}>
                        <Tab label="Time entries" className="tm-tab" />
                        <Tab label="PTO" className="tm-tab" />
                        {showApprovals ? <Tab label={`Approvals${pendingTotal ? ` (${pendingTotal})` : ""}`} className="tm-tab" /> : null}
                    </Tabs>
                </div>
            }

            <div className="tm-toolbar">
                <div>
                    <p className="tm-eyebrow">{employeeDetailPage ? "Employee time" : "Time manager"}</p>
                    <h1>{pageTitle}</h1>
                </div>
                {tabIndex === 0 ? (
                    <div className="tm-toolbar-controls">
                        <DateRangePicker
                            startDate={dateRange.startDate}
                            endDate={dateRange.endDate}
                            onChange={setDateRange}
                        />
                        <button className="tm-primary-button" onClick={() => handleAddNewEntry()}>+ Add time entry</button>
                    </div>
                ) : null}
            </div>

            {/* this is the time entries page tab */}
            {tabIndex === 0 && (
                <main className="tm-content">
                    {showApprovals && pendingTotal > 0 ? (
                        <button className="tm-approval-banner" onClick={() => setTabIndex(2)}>
                            <span className="tm-banner-count">{pendingTotal}</span>
                            <span>
                                <strong>{pendingTotal === 1 ? "1 request is" : `${pendingTotal} requests are`} waiting for approval</strong>
                                <small>{pendingTimeCount} time adjustment{pendingTimeCount === 1 ? "" : "s"} · {pendingPtoCount} PTO</small>
                            </span>
                            <span className="tm-banner-action">Review →</span>
                        </button>
                    ) : null}

                    <section className="tm-stats" aria-label="Summary for the selected dates">
                        <div className="tm-stat"><span>Total hours</span><strong>{formatHours(totalMinutesWorked)}</strong></div>
                        <div className="tm-stat"><span>Days worked</span><strong>{daysWorked}</strong></div>
                        <div className={`tm-stat${daysWithMissingPunch ? " warning" : ""}`}>
                            <span>Days with a missing punch</span><strong>{daysWithMissingPunch}</strong>
                        </div>
                    </section>

                    {myPendingRequests?.length ? (
                        <section className="tm-card">
                            <div className="tm-section-heading">
                                <div><h2>Waiting for approval</h2><p>An admin will review these changes.</p></div>
                            </div>
                            <ul className="tm-request-list">
                                {myPendingRequests.map((request) => (
                                    <li key={request.id} className="tm-request-row">
                                        <span className={`tm-badge ${request.original ? "edit" : "new"}`}>{request.original ? "Edit" : "New"}</span>
                                        <div className="tm-request-main">
                                            <strong>{formatEntryType(request.updated.type)} · {dayjs(request.date).format("ddd, MMM D")}</strong>
                                            {renderRequestChange(request)}
                                        </div>
                                        <span className="tm-status pending">Pending</span>
                                    </li>
                                ))}
                            </ul>
                        </section>
                    ) : null}

                    <section className="tm-day-list">
                        {sortedEntryDays.length === 0 ? (
                            <div className="tm-card tm-empty">No time entries in these dates.</div>
                        ) : sortedEntryDays.map(([date, day]) => {
                            const missingPunch = day.events.length % 2 === 1;
                            const sortedEvents = [...day.events].sort((first, second) =>
                                (first.timestamp?.toMillis?.() ?? 0) - (second.timestamp?.toMillis?.() ?? 0)
                            );
                            return (
                                <article key={date} className={`tm-card tm-day${missingPunch ? " needs-review" : ""}`}>
                                    <header className="tm-day-header">
                                        <div>
                                            <h3>{dayjs(date).format("dddd, MMM D")}</h3>
                                            {missingPunch ? <span className="tm-badge warning" title="This day has an odd number of punches, so a clock in or out may be missing.">Missing punch?</span> : null}
                                        </div>
                                        <div className="tm-day-meta">
                                            <strong>{formatHours(day.minutesWorked)} hrs</strong>
                                            <button className="tm-text-button" onClick={() => handleAddNewEntry(date)}>+ Add</button>
                                        </div>
                                    </header>
                                    <ul className="tm-punch-list">
                                        {sortedEvents.map((entry, index) => (
                                            <li key={`${entry.type}-${entry.timestamp?.seconds}-${index}`} className="tm-punch">
                                                <span className={`tm-punch-dot ${getPunchVariant(entry.type)}`} aria-hidden="true" />
                                                <span className="tm-punch-type">
                                                    {formatEntryType(entry.type)}
                                                    {entry.source ? <small>{entry.source}</small> : null}
                                                </span>
                                                <span className="tm-punch-time">{formatClock(entry.timestamp)}</span>
                                                <span className="tm-punch-actions">
                                                    <button className="tm-text-button" onClick={() => handleEditEntry(entry)}>Edit</button>
                                                    <button className="tm-text-button danger" onClick={() => handleRemoveEntry(entry)}>Remove</button>
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                </article>
                            );
                        })}
                    </section>
                </main>
            )}

            {/* this is the PTO tab */}
            {tabIndex === 1 && (
                <main className="tm-content">
                    <section className="tm-stats" aria-label="PTO this year">
                        <div className="tm-stat"><span>PTO used this year</span><strong>{totalHoursTaken} hrs</strong></div>
                        <div className="tm-stat"><span>PTO earned this year</span><strong>{totalPtoAllowed.toFixed(2)} hrs</strong></div>
                    </section>

                    <div className="tm-two-column">
                        <section className="tm-card tm-form-card">
                            <div className="tm-section-heading">
                                <div><h2>Request time off</h2><p>Requests go to an admin for approval.</p></div>
                            </div>
                            <label className="tm-field">
                                <span>First day off</span>
                                <input type="date" value={ptoStartDate} onChange={(e) => setPtoStart(e.target.value)} />
                            </label>
                            <label className="tm-field">
                                <span>Last day off</span>
                                <input type="date" value={ptoEndDate} min={ptoStartDate || undefined} onChange={(e) => setPtoEnd(e.target.value)} />
                            </label>
                            {ptoRequestDays ? <p className="tm-hint">{ptoRequestDays} day{ptoRequestDays === 1 ? "" : "s"} requested</p> : null}
                            <button className="tm-primary-button" onClick={() => handleSubmitPtoRequest()}>Submit request</button>
                            {upcomingBlackoutDates.length ? (
                                <div className="tm-blackouts">
                                    <span>Upcoming blackout days (PTO can't be requested)</span>
                                    <div>{upcomingBlackoutDates.map((date) => <span key={date} className="tm-chip">{dayjs(date).format("ddd, MMM D")}</span>)}</div>
                                </div>
                            ) : null}
                        </section>

                        <section className="tm-card">
                            <div className="tm-section-heading">
                                <div><h2>My requests this year</h2><p>{sortedMyPtoRequests.length} request{sortedMyPtoRequests.length === 1 ? "" : "s"}</p></div>
                            </div>
                            {myPtoRequests === null ? <p className="tm-muted">Loading…</p>
                                : sortedMyPtoRequests.length === 0 ? <p className="tm-muted">No PTO requests yet this year.</p> : (
                                    <ul className="tm-request-list">
                                        {sortedMyPtoRequests.map((request) => {
                                            const status = request.status?.toLowerCase() ?? "pending";
                                            return (
                                                <li key={request.id} className="tm-request-row">
                                                    <div className="tm-request-main">
                                                        <strong>{formatPtoRange(request.startDate, request.endDate)}</strong>
                                                        <small>
                                                            {getPtoDayCount(request.startDate, request.endDate)} day{getPtoDayCount(request.startDate, request.endDate) === 1 ? "" : "s"}
                                                            {status === "approved" ? ` · ${typeof request.approvedHours === "number" ? `${request.approvedHours} hours` : `${request.approvedDays} days`} used` : ""}
                                                        </small>
                                                    </div>
                                                    <span className={`tm-status ${status}`}>{status}</span>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                )}
                        </section>
                    </div>
                </main>
            )}

            {/* admin approvals tab */}
            {tabIndex === 2 && showApprovals && (
                <main className="tm-content">
                    <section className="tm-card">
                        <div className="tm-section-heading">
                            <div><h2>Time adjustments</h2><p>{pendingTimeCount ? `${pendingTimeCount} waiting · grouped by employee` : "All caught up"}</p></div>
                        </div>
                        {pendingRequests === null ? <p className="tm-muted">Loading…</p>
                            : pendingRequestGroups.length === 0 ? <p className="tm-muted">No pending time adjustments.</p>
                                : pendingRequestGroups.map((group) => (
                                    <div key={group.userId} className="tm-request-group">
                                        <div className="tm-group-header">
                                            <strong>{group.name}</strong>
                                            <span>{group.requests.length} request{group.requests.length === 1 ? "" : "s"}</span>
                                            {group.requests.length > 1 ? (
                                                <button className="tm-secondary-button" disabled={busyApprovalKey !== null} onClick={() => handleApproveAll(group)}>
                                                    {busyApprovalKey === `user:${group.userId}` ? "Approving…" : `Approve all ${group.requests.length}`}
                                                </button>
                                            ) : null}
                                        </div>
                                        <ul className="tm-request-list">
                                            {group.requests.map((request) => (
                                                <li key={request.id} className="tm-request-row">
                                                    <span className={`tm-badge ${request.original ? "edit" : "new"}`}>{request.original ? "Edit" : "New"}</span>
                                                    <div className="tm-request-main">
                                                        <strong>{formatEntryType(request.updated.type)} · {dayjs(request.date).format("ddd, MMM D")}</strong>
                                                        {renderRequestChange(request)}
                                                        <small>Requested {formatRequestedAt(request.requestedAt)}</small>
                                                    </div>
                                                    <div className="tm-request-actions">
                                                        <button className="tm-secondary-button" disabled={busyApprovalKey !== null}
                                                            onClick={() => runApprovalAction(request.id, () => handleDeclineTimeRequest(request))}>Decline</button>
                                                        <button className="tm-approve-button" disabled={busyApprovalKey !== null}
                                                            onClick={() => runApprovalAction(request.id, () => handleApproveRequest(request))}>Approve</button>
                                                    </div>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                ))}
                    </section>

                    <section className="tm-card">
                        <div className="tm-section-heading">
                            <div><h2>PTO requests</h2><p>{pendingPtoCount ? `${pendingPtoCount} waiting · oldest first` : "All caught up"}</p></div>
                        </div>
                        {pendingPtoRequests === null ? <p className="tm-muted">Loading…</p>
                            : pendingPtoRequests.length === 0 ? <p className="tm-muted">No pending PTO requests.</p> : (
                                <ul className="tm-request-list">
                                    {pendingPtoRequests.map((request) => {
                                        const requestDays = getPtoDayCount(request.startDate, request.endDate);
                                        return (
                                            <li key={request.id} className="tm-request-row">
                                                <div className="tm-request-main">
                                                    <strong>{request.firstName} {request.lastName}</strong>
                                                    <span className="tm-change"><strong>{formatPtoRange(request.startDate, request.endDate)}</strong><em>{requestDays} day{requestDays === 1 ? "" : "s"}</em></span>
                                                    <small>Requested {formatRequestedAt(request.requestedAt)} · {request.ptoUsed ?? 0} hrs PTO used so far</small>
                                                </div>
                                                <div className="tm-request-actions">
                                                    <button className="tm-secondary-button" disabled={busyApprovalKey !== null}
                                                        onClick={() => runApprovalAction(request.id, () => handleDeclinePto(request))}>Decline</button>
                                                    <button className="tm-approve-button" disabled={busyApprovalKey !== null} onClick={() => handleApprovePto(request)}>Approve…</button>
                                                </div>
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}
                    </section>
                </main>
            )}

            <Dialog open={entryDialogOpen} onClose={() => !isSubmittingEntry && resetState()} fullWidth maxWidth="xs" PaperProps={{ className: "tm-dialog" }}>
                <div className="tm-dialog-body">
                    <p className="tm-eyebrow">{editEntry ? "Edit punch" : "New punch"}</p>
                    <h2>{editEntry ? `Change ${formatEntryType(editEntry.type).toLowerCase()} time` : "Add a time entry"}</h2>
                    <p className="tm-hint">
                        {editEntry
                            ? `Currently ${formatClock(editEntry.timestamp)} on ${dayjs(adjustmentDate).format("dddd, MMM D")}.`
                            : "This adds a new punch. To fix an existing punch, use Edit on that punch instead."}
                    </p>
                    <label className="tm-field">
                        <span>Entry type</span>
                        <select disabled={editEntry !== null} value={adjustmentType} onChange={(e) => setAdjustmentType(e.target.value)}>
                            <option value="">Select a type</option>
                            {adjustments.map((adj) => <option key={adj.id} value={adj.id}>{adj.type}</option>)}
                        </select>
                    </label>
                    <div className="tm-field-row">
                        <label className="tm-field">
                            <span>Date</span>
                            <input disabled={editEntry !== null} type="date" value={adjustmentDate} onChange={(e) => setAdjustmentDate(e.target.value)} />
                        </label>
                        <label className="tm-field">
                            <span>Time</span>
                            <input type="time" value={adjustmentTime} onChange={(e) => setAdjustmentTime(e.target.value)} />
                        </label>
                    </div>
                    {!userData?.admin ? <p className="tm-hint">Your change is sent to an admin for approval.</p> : null}
                    <div className="tm-dialog-actions">
                        <button className="tm-secondary-button" disabled={isSubmittingEntry} onClick={resetState}>Cancel</button>
                        <button className="tm-primary-button" disabled={isSubmittingEntry} onClick={handleSubmitForm}>
                            {isSubmittingEntry ? "Saving…" : userData?.admin ? (editEntry ? "Save change" : "Add entry") : "Send for approval"}
                        </button>
                    </div>
                </div>
            </Dialog>

            <Snackbar
                open={message !== ""}
                message={message}
                autoHideDuration={3000}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            />
            <ConfirmDialog
                open={dialogOpen}
                onCancel={() => setDialogOpen(false)}
                onConfirm={handleConfirmSubmission}
                title={dialogTitle}
                message={dialogMessage}
            />
            <PtoDays
                open={ptoDaysDialog}
                onCancel={() => setOpenPtoDaysDialog(false)}
                onConfirm={handleFinalizePto}
                ptoRequest={newPtoRequest}
                ptoHoursPerDay={approvalPtoHoursPerDay}
            />
        </div>
    );
};

require('./styles.scss')
